import { AddRounded, ContentCopy, DataArrayRounded, DataObjectRounded, Delete, InfoRounded, KeyboardDoubleArrowRightRounded, ShapeLineRounded } from '@mui/icons-material'
import { Badge, Box, Button, Chip, FormControlLabel, IconButton, MenuItem, Paper, Popover, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material'
import { ReactNode, useState } from 'react'
import algo from '../../core/Algo'
import { IApprovalRequest } from '../../definitions/Approval'
import { IFlow, INode, JsonSchema } from '../../definitions/Nodes'
import Helper from '../../helpers/Helper'

interface P {
    selectedNode: INode
    flow: IFlow
    setSelected: (node: INode) => void
    approval: IApprovalRequest
}

const getTypeColor = (type: string | undefined) => {
    if (typeof type === 'undefined') return 'default'
    switch (type) {
        case 'string':
            return 'primary'
        case 'number':
            return 'audit'
        case 'boolean':
            return 'subFlow'
        case 'array':
            return 'warning'
        case 'object':
            return 'dryRun'
        default:
            return 'default'
    }
}

const renderInfoField = (title: string | ReactNode, disable?: boolean) => (
    <Tooltip arrow title={title} disableHoverListener={disable}>
        <InfoRounded fontSize='inherit' sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
    </Tooltip>
)

const NodeShapeConfig = ({ selectedNode, setSelected, approval }: P) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const [schemaType, setSchemaType] = useState<'object' | 'array'>(selectedNode?.jsonSchema?.type === 'array' ? 'array' : 'object')
    const canEdit = approval.status === 'draft' || approval.status === 'rejected'

    const schemaProps = (selectedNode?.jsonSchema?.type === 'array' && selectedNode.jsonSchema.items?.properties)
        ? selectedNode.jsonSchema.items.properties
        : (selectedNode?.jsonSchema?.type === 'object' && selectedNode.jsonSchema?.properties)
                ? selectedNode.jsonSchema.properties
                : {}

    const initialRows = Object.entries(schemaProps).map(([key, value]: any) => ({
        field: key,
        type: Array.isArray(value.type) ? value.type.join(' | ') : value.type
    }))

    const [rows, setRows] = useState(initialRows)

    const buildPropertiesFromRows = (rows: { field: string, type: string }[]) => {
        const properties: Record<string, any> = {}

        rows.forEach(row => {
            if (!row.field) return

            properties[row.field] = {
                type: row.type
            }
        })

        return properties
    }

    const updateRows = (newRows: typeof rows, type = schemaType) => {
        setRows(newRows)

        const newSchema = type === 'array'
            ? {
                    $schema: 'https://json-schema.org/draft-07/schema',
                    title: 'RootSchema',
                    type: 'array',
                    items: {
                        type: 'object',
                        properties: buildPropertiesFromRows(newRows)
                    }
                }
            : {
                    $schema: 'https://json-schema.org/draft-07/schema',
                    title: 'RootSchema',
                    type: 'object',
                    properties: buildPropertiesFromRows(newRows)
                }

        setSelected({
            ...selectedNode,
            jsonSchema: newSchema as JsonSchema
        })
    }

    const handleDuplicate = (index: number) => {
        const newRows = [...rows]
        newRows.splice(index + 1, 0, { ...rows[index] })
        updateRows(newRows)
    }

    const handleDelete = (index: number) => {
        setRows(rows.filter((_, i) => i !== index))
    }

    const handleFieldChange = (index: number, value: string) => {
        const newRows = [...rows]
        newRows[index].field = value
        updateRows(newRows)
    }

    const handleTypeChange = (index: number, value: string) => {
        const newRows = [...rows]
        newRows[index].type = value
        updateRows(newRows)
    }

    const handleAddRow = () => {
        const newRows = [...rows, { field: '', type: 'string' }]
        updateRows(newRows)
    }

    const handleSwitch = () => {
        setSelected({
            ...selectedNode,
            overwriteShape: !selectedNode.overwriteShape
        })
    }

    const handleSchemaTypeChange = (_: React.MouseEvent<HTMLElement>, newType: 'object' | 'array' | null) => {
        if (!newType) return
        setSchemaType(newType)
        updateRows(rows, newType)
    }

    return (
        <Box>
            <Tooltip title='Open shape config' arrow>
                <IconButton
                    color='primary'
                    sx={{
                        backgroundColor: algo.hasVal(anchorEl) && Helper.hexToRgbA(theme.palette.primary.main, 0.3)
                    }}
                    disabled={!canEdit}
                    onClick={e =>
                        algo.hasVal(anchorEl)
                            ? setAnchorEl(null)
                            : setAnchorEl(e.currentTarget)}>
                    <Badge variant='dot' color='primary' invisible={algo.hasVal(anchorEl) || !selectedNode?.overwriteShape}>
                        <ShapeLineRounded color='action' fontSize='small' />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                disableEnforceFocus
                disableAutoFocus
                sx={{
                    top: 'calc(-1 * var(--unit) * 6)',
                    left: 'calc(-1 * var(--unit) * 12)',
                    pointerEvents: 'none'
                }}
                slotProps={{ paper: { sx: { pointerEvents: 'auto' } } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Paper sx={{ width: '30vw', maxHeight: '80vh' }}>
                    <Stack
                        sx={{
                            paddingX: 2, paddingY: 1.5, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: theme.palette.background.paper
                        }}>
                        <Stack flexDirection='row' gap={2} alignItems='center'>
                            <ShapeLineRounded fontSize='large' />
                            <Stack>
                                <Typography variant='overline' color={theme.palette.text.secondary}>SHAPE</Typography>
                                <Typography variant='subtitle2'>Custom Shape</Typography>
                            </Stack>
                        </Stack>
                        <Stack direction='row' alignItems='center' gap={3}>
                            <IconButton onClick={() => setAnchorEl(null)}>
                                <KeyboardDoubleArrowRightRounded />
                            </IconButton>
                        </Stack>
                    </Stack>
                    <TableContainer sx={{ padding: 2, height: 450 }}>
                        <Table
                            size='small'
                            sx={{
                                '& .MuiTableCell-root': {
                                    borderBottom: 'none'
                                }
                            }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <strong>Field</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>Type</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>Actions</strong>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell sx={{ width: '40%' }}>
                                            <TextField
                                                fullWidth
                                                size='small'
                                                value={row.field}
                                                disabled={!selectedNode?.overwriteShape}
                                                onChange={e => handleFieldChange(index, e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                size='small'
                                                value={row.type}
                                                disabled={!selectedNode?.overwriteShape}
                                                onChange={e => handleTypeChange(index, e.target.value)}
                                                fullWidth
                                                renderValue={selected => (
                                                    <Chip
                                                        label={selected}
                                                        color={getTypeColor(selected)}
                                                        size='small'
                                                    />
                                                )}>
                                                <MenuItem value='string'>string</MenuItem>
                                                <MenuItem value='number'>number</MenuItem>
                                                <MenuItem value='boolean'>boolean</MenuItem>
                                                <MenuItem value='array'>array</MenuItem>
                                                <MenuItem value='object'>object</MenuItem>
                                                <MenuItem value='null'>null</MenuItem>
                                            </Select>
                                        </TableCell>
                                        <TableCell sx={{ width: '22%' }}>
                                            <IconButton
                                                size='small'
                                                disabled={!selectedNode?.overwriteShape}
                                                onClick={() => handleDuplicate(index)}>
                                                <ContentCopy fontSize='small' />
                                            </IconButton>
                                            <IconButton
                                                size='small'
                                                disabled={!selectedNode?.overwriteShape}
                                                color='error'
                                                onClick={() => handleDelete(index)}>
                                                <Delete fontSize='small' />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Stack>
                            <Button
                                sx={{ flex: 1, marginTop: 1 }}
                                disabled={!selectedNode?.overwriteShape}
                                size='small'
                                variant='text'
                                startIcon={<AddRounded fontSize='small' />}
                                onClick={handleAddRow}>
                                add shape row
                            </Button>
                        </Stack>
                    </TableContainer>
                    <Stack flexDirection='row' alignItems='center' gap={1} justifyContent='space-between'>
                        <Stack direction='row' alignItems='center'>
                            <FormControlLabel
                                control={(<Switch onClick={handleSwitch} size='small' />)}
                                checked={selectedNode?.overwriteShape ?? false}
                                label={(
                                    <Stack flexDirection='row' alignItems='center' gap={1}>
                                        {renderInfoField(
                                            <p>
                                                This option will replace
                                                {' '}
                                                <br />
                                                {' '}
                                                the default compiled shape
                                            </p>
                                        )}
                                        <Typography variant='body2'>Overwrite shape</Typography>
                                    </Stack>
                                )}
                                labelPlacement='start'
                            />
                        </Stack>
                        <Stack flexDirection='row' alignItems='center' gap={1} padding={2}>
                            <Typography variant='body2'>Shape format:</Typography>
                            <ToggleButtonGroup
                                disabled={!selectedNode?.overwriteShape}
                                value={schemaType}
                                exclusive
                                onChange={handleSchemaTypeChange}
                                size='small'>
                                <Tooltip title='Object' arrow>
                                    <ToggleButton size='small' value='object'>
                                        <DataObjectRounded fontSize='small' />
                                    </ToggleButton>
                                </Tooltip>
                                <Tooltip title='Array' arrow>
                                    <ToggleButton size='small' value='array'>
                                        <DataArrayRounded fontSize='small' />
                                    </ToggleButton>
                                </Tooltip>
                            </ToggleButtonGroup>
                        </Stack>
                    </Stack>
                </Paper>
            </Popover>
        </Box>
    )
}

export default NodeShapeConfig
